"use client";

import { useEffect, useRef, useState } from "react";
import { useTranslations } from "next-intl";

const REQUEST = "curl https://zaruvia.com/api/public/matrix/15/3/1990";

// JSON-ответ как массив строк — анимируем построчно (быстрее и выразительнее
// чем посимвольное «typing»).
const RESPONSE_LINES = [
  "{",
  '  "input":  { "day": 15, "month": 3, "year": 1990 },',
  '  "matrix": {',
  '    "personal":      { "arcana": 15, "name": "El Diablo",            "emoji": "🔥" },',
  '    "social":        { "arcana": 3,  "name": "La Emperatriz",        "emoji": "🌺" },',
  '    "soul":          { "arcana": 18, "name": "La Luna",              "emoji": "🌙" },',
  '    "body":          { "arcana": 10, "name": "La Rueda de la Fortuna","emoji": "🎡" },',
  '    "center":        { "arcana": 10, "name": "La Rueda de la Fortuna","emoji": "🎡" },',
  '    "money":         { "arcana": 7,  "name": "El Carro",             "emoji": "⚡" },',
  '    "destiny":       { "arcana": 14, "name": "La Templanza",         "emoji": "🧪" }',
  "  },",
  '  "license":     "CC-BY 4.0",',
  '  "details_url": "https://zaruvia.com/profile-preview?birth=1990-03-15"',
  "}",
];

const LINE_DELAY_MS = 110;       // скорость «потока» ответа
const HOLD_AFTER_COMPLETE_MS = 9000; // сколько держим финальный кадр
const PRE_REQUEST_DELAY_MS = 700;    // пауза после показа request, до начала response

export function ApiTerminal() {
  const t = useTranslations("apiTerminal");
  const [linesShown, setLinesShown] = useState(0);
  const [phase, setPhase] = useState<"waiting" | "streaming" | "done">("waiting");
  const timersRef = useRef<Array<ReturnType<typeof setTimeout>>>([]);

  useEffect(() => {
    function clearAll() {
      timersRef.current.forEach(clearTimeout);
      timersRef.current = [];
    }

    function runCycle() {
      setLinesShown(0);
      setPhase("waiting");
      // Пауза «отправки запроса»
      timersRef.current.push(
        setTimeout(() => {
          setPhase("streaming");
          // Стрим построчно
          for (let i = 1; i <= RESPONSE_LINES.length; i++) {
            timersRef.current.push(
              setTimeout(() => setLinesShown(i), i * LINE_DELAY_MS),
            );
          }
          // Готово → подержать → новый цикл
          timersRef.current.push(
            setTimeout(() => {
              setPhase("done");
              timersRef.current.push(setTimeout(runCycle, HOLD_AFTER_COMPLETE_MS));
            }, RESPONSE_LINES.length * LINE_DELAY_MS + 200),
          );
        }, PRE_REQUEST_DELAY_MS),
      );
    }

    runCycle();
    return clearAll;
  }, []);

  return (
    <div className="bg-stone-950 rounded-2xl shadow-2xl border border-stone-800 overflow-hidden font-mono text-[11px] sm:text-xs leading-relaxed w-full max-w-full min-w-0">
      {/* Terminal header */}
      <div className="flex items-center gap-2 bg-stone-900 border-b border-stone-800 px-4 py-2.5">
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-full bg-red-500/80" />
          <div className="w-3 h-3 rounded-full bg-yellow-500/80" />
          <div className="w-3 h-3 rounded-full bg-green-500/80" />
        </div>
        <div className="text-[10px] text-stone-500 ml-3 truncate">
          zaruvia-api · live preview
        </div>
      </div>

      {/* Body */}
      <div className="p-4 sm:p-5 text-stone-300 overflow-x-auto">
        {/* Request line — $ и команда одним блоком с явным пробелом
            (чтобы при copy-paste из браузера получалось «$ curl ...», а не «$curl») */}
        <div className="break-all mb-1.5">
          <span className="text-emerald-400">$</span>
          <span className="text-violet-300">{" "}{REQUEST}</span>
        </div>

        {/* Status / streaming indicator */}
        <div className="text-[10px] text-stone-500 mb-3 pl-4">
          {phase === "waiting" && t("sending")}
          {phase === "streaming" && "← 200 OK · application/json"}
          {phase === "done" && t("done")}
        </div>

        {/* Response */}
        <pre
          className="whitespace-pre text-stone-300 leading-relaxed min-h-[260px] sm:min-h-[300px]"
          aria-live="polite"
        >
          {RESPONSE_LINES.slice(0, linesShown).map((line, idx) => (
            <div key={idx}>{line}</div>
          ))}
          {/* Blinking cursor */}
          {phase !== "done" && (
            <span className="inline-block w-2 h-3.5 bg-violet-400 align-middle animate-pulse ml-0.5" />
          )}
        </pre>
      </div>

      {/* Footer */}
      <div className="bg-stone-900 border-t border-stone-800 px-4 py-2 flex items-center justify-between text-[10px] text-stone-500">
        <span>{t("footer")}</span>
        <span className="text-violet-400/80">/api/public/matrix</span>
      </div>
    </div>
  );
}
